<template>
    <div class="br-table">
        <table class="table table-hover">
            <thead>
            <tr>
                <th v-for="column in columns"
                    :class="getHeaderClass(column)"
                    @click="setOrderBy(column)">
              <span class="br-table__heading --no-select">
                  <slot :name="`header-${column}`" v-bind:column="column">
                      {{ get(options.headings, column, prepareHeading(column)) }}
                  </slot>
              </span>
                    <span v-if="isSorteable(column)" class="br-table__sort-icon --no-select float-right">
            <i class="material-icons" v-show="isOrderAndSortBy(column, 'asc')">arrow_drop_up</i>
            <i class="material-icons" v-show="isOrderAndSortBy(column, 'desc')">arrow_drop_down</i>
          </span>
                </th>
            </tr>
            </thead>
            <tbody>
            <tr v-show="showLoadingProgress">
                <td :colspan="columns.length">
                    <spinner></spinner>
                </td>
            </tr>
            <tr v-for="row in rows" v-show="!showLoadingProgress">
                <td v-for="column in columns" :class="getColumnClass(column)">
                    <slot :name="column" v-bind:row="row">
                        {{ get(row, column) }}
                    </slot>
                </td>
            </tr>
            <tr v-show="showNoItems">
                <td :colspan="columns.length" class="text-center">
                    <slot name="no-data-text">
                        {{ noDataText }}
                    </slot>
                </td>
            </tr>
            </tbody>
        </table>
        <div v-show="!showLoadingProgress" v-if="showPagination && total > localPerPage" class="row simple-table__pagination-footer">
            <slot name="pagination" v-bind:data="{total, curPage, localPerPage, paginateFrom, paginateTo}">
                <div class="col-md-6 br-table__pagination-footer__label">
                    <slot name="pagination-label" v-bind:data="{total, curPage, localPerPage, paginateFrom, paginateTo}">
                        Showing {{ paginateFrom }} to {{ paginateTo }} of {{ total }} records
                    </slot>
                </div>
                <div class="col-md-6">
                    <slot name="pagination-paginate" v-bind:data="{total, curPage, localPerPage, paginateFrom, paginateTo}">
                        <paginate v-model="curPage" :total-count="total" :per-page="localPerPage"></paginate>
                    </slot>
                </div>
            </slot>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import get from 'lodash/get'
    import Paginate from './br-paginate'
    import orderBy from 'lodash/orderBy'
    import isEmpty from 'lodash/isEmpty'
    import Spinner from 'vue-simple-spinner'

    export default {
        name: 'BrTable',

        props: {
            columns:        {
                type:    Array,
                default: () => ([])
            },
            data:           {
                type:    Array,
                default: () => ([])
            },
            options:        {
                type:    Object,
                default: () => ({})
            },
            url:            {
                type:    String,
                default: null
            },
            clientSide:     {
                type:    Boolean,
                default: false
            },
            showPagination: {
                type:    Boolean,
                default: true
            },
            perPage:        {
                type:    Number,
                default: 30
            },
            currentPage:    {
                type:    Number,
                default: 1
            },
            params:         {
                type:    Object,
                default: () => ({})
            },
            showLoading:    {
                type:    Boolean,
                default: true
            },
            orderBy:        {
                type:    String,
                default: null
            },
            sortBy:         {
                type:    String,
                default: 'asc'
            },
            noDataText:     {
                type:    String,
                default: 'There is no data to show'
            }
        },

        data() {
            return {
                isLoading:    true,
                curPage:      this.currentPage,
                localPerPage: this.perPage,
                localData:    this.data,
                total:        this.data.length,
                localOrderBy: this.orderBy,
                localSortBy:  this.sortBy.toLowerCase()
            }
        },

        watch: {
            url() {
                if (this.isValidUrl) {
                    this.fetchData(true)
                }
            },

            // Triggered when pagination change
            curPage() {
                if (this.useRemoteForData) {
                    this.fetchData(true)
                }
            },

            perPage(newValue) {
                this.localPerPage = newValue
            },

            data(newValue, oldValue) {
                this.localData = newValue

                if (!isEmpty(oldValue)) {
                    this.total = this.localData.length
                    if (this.curPage > Math.ceil(this.total / this.localPerPage)) {
                        this.curPage = 1
                    }
                    return
                }

                this.setPaginationData(this.localData.length, 1)
            },

            localPerPage() {
                this.curPage = 1
                if (this.useRemoteForData) {
                    this.fetchData()
                }
            }
        },

        computed: {
            isValidUrl() {
                return this.url !== null && this.url !== ''
            },

            useRemoteForData() {
                return this.isValidUrl && !this.clientSide
            },

            rows() {
                // Use data feed from remote URL
                if (this.useRemoteForData) {
                    return this.localData
                }

                // Use from local data and apply ordering + pagination
                const data = orderBy(this.localData, [this.localOrderBy], [this.localSortBy])

                const offset = (this.curPage - 1) * this.localPerPage
                return data.slice(offset, offset + this.localPerPage)
            },

            showLoadingProgress() {
                return this.isLoading && this.showLoading
            },

            paginateFrom() {
                return ((this.curPage - 1) * this.localPerPage) + 1
            },

            paginateTo() {
                const to = this.paginateFrom + this.localPerPage
                return to > this.total ? this.total : to
            },

            showNoItems() {
                return !this.isLoading && isEmpty(this.localData)
            }
        },

        methods: {
            prepareHeading(str) {
                return (str + '')
                    .replace('.', ' ')
                    .replace(/^(.)|\s+(.)/g, function($1) {
                        return $1.toUpperCase()
                    })
            },

            get(object, keys, defaultVal = null) {
                return get(object, keys, defaultVal)
            },

            fetchData(showLoading = false) {
                const queryString = Object.keys(this.params).map((key) => {
                    return encodeURIComponent(key) + '=' + (
                        this.isEmpty(this.params[key]) ? '' : encodeURIComponent(this.params[key])
                    )
                }).join('&')

                let url
                let separation = this.url.indexOf('?') > 0 ? '&' : '?'

                if (this.clientSide) {
                    url = `${this.url}${queryString ? separation + queryString : ''}`
                } else {
                    const orderByQuery = this.orderBy || this.localOrderBy ? `&order_by=${this.localOrderBy}&sort_by=${this.localSortBy}` : ''
                    url = `${this.url}${separation}page=${this.curPage}&per_page=${this.localPerPage}${orderByQuery}${queryString ? '&' + queryString : ''}`
                    separation = '&'
                }

                this.isLoading = showLoading
                axios.get(url)
                    .then(({ data }) => {
                        this.localData = data.data

                        if (this.clientSide) {
                            const to = this.localData.length > this.localPerPage
                                ? this.localPerPage
                                : this.localData.length
                            this.setPaginationData(this.localData.length, 1, 1, to)
                        } else {
                            this.setPaginationData(data.total, data.current_page, data.from, data.to)
                        }
                        this.isLoading = false
                    })
                    .catch(() => {
                        this.isLoading = false
                    })
            },

            setPaginationData(total, curPage) {
                this.total = total
                this.curPage = curPage
            },

            setOrderBy(column) {
                if (!this.isSorteable(column)) {
                    return
                }

                this.localOrderBy = column
                this.localSortBy = this.isOrderBy(column) && this.localSortBy.toLowerCase() === 'asc' ? 'desc' : 'asc'

                // Fetch from remote with the updated order + sort
                if (this.useRemoteForData) {
                    this.fetchData()
                }
            },

            isOrderBy(column) {
                return this.localOrderBy === column
            },

            isSorteable(column) {
                return get(this.options, 'sortable', []).includes(column)
            },

            isOrderAndSortBy(column, sort) {
                return this.isOrderBy(column) && this.localSortBy.toLowerCase() === sort
            },

            getHeaderClass(column) {
                let className = get(this.options, `headersClasses.${column}`, '')
                className = className + ' ' + (this.isSorteable(column) ? 'br-table__sorteable' : '')
                className = className + ' ' + this.getColumnClass(column)

                return className.replace(/\s+/g, ' ').trim()
            },

            getColumnClass(column) {
                let className = get(this.options, `columnsClasses.${column}`, '')
                className = className + ' ' + (this.isOrderBy(column) ? 'br-table__sorted' : '')

                return className.replace(/\s+/g, ' ').trim()
            },

            reload() {
                return this.fetchData(true)
            },

            refresh() {
                return this.fetchData()
            }
        },

        mounted() {
            // If initial url has been set then fetch data
            if (this.isValidUrl) {
                this.fetchData(true)
            }
        },

        components: {
            Spinner,
            Paginate
        }
    }
</script>

<style>
    .br-table {
        background-color: #FFFFFF;
        padding: 15px 20px;
    }

    .br-table table {
        margin-bottom: 0 !important;
    }

    .br-table__heading {
        cursor: pointer;
        padding-top: 2px;
        color: #3139f2;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        font-size: 14px;
    }

    .br-table thead tr th {
        border-bottom: 1px solid #71767f;
    }

    .br-table tbody tr td {
        border-top: 1px solid #cfcfd4;
    }

    .br-table thead tr th,
    .br-table tbody tr td {
        padding: 13px 15px !important;
    }

    .br-table thead tr th:first-child,
    .br-table tbody tr td:first-child {
        padding-left: 20px !important;
    }

    .br-table thead tr th:last-child,
    .br-table tbody tr td:last-child {
        padding-right: 20px !important;
    }

    .br-table__sorteable {
        cursor: pointer;
    }

    .br-table__sort-icon {
        padding-top: 1px;
        width: 15px;
        text-align: center;
        float: right;
    }

    .br-table__sort-icon .material-icons {
        font-size: 20px;
    }

    .br-table__pagination-footer__label {
        font-size: 18px;
        padding-top: 24px;
    }
</style>
